/* eslint-disable max-depth */
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { firstValueFrom, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { MilestoneQuery } from '@models/milestone-category/milestone/milestone.query';
import { MilestoneModel } from '@models/milestone-category/milestone/milestone.store';
import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import {
  TimelineService,
  UpdateDependencyInput,
} from '@models/milestone-category/milestone/timeline.service';
import { MilestoneCategoryQuery } from '@models/milestone-category/milestone-category.query';

import { InputComponent } from '@shared/components/input/input.component';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { listTimelineMilestonesQuery } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';

import {
  TimelineDialogDependencyFn,
  TimelineDialogDependencyGraphFn,
  TimelineDialogFormValue,
} from './timeline-dialog.model';
import { TimelineDependencyComponent } from '../timeline-dependency/timeline-dependency.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    AsyncPipe,
    InputComponent,
    ModalComponent,
    FormErrorDirective,
  ],
  selector: 'aux-timeline-dialog',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  templateUrl: './timeline-dialog.component.html',
})
export class TimelineDialogComponent {
  milestoneCategoryQuery = inject(MilestoneCategoryQuery);

  private formBuilder = inject(UntypedFormBuilder);
  private milestoneQuery = inject(MilestoneQuery);
  private overlayService = inject(OverlayService);
  private timelineQuery = inject(TimelineQuery);
  private timelineService = inject(TimelineService);

  addTimelineMilestoneForm = this.formBuilder.group({
    actual_end_date: null,
    actual_start_date: null,
    contract_end_date: '',
    contract_start_date: '',
    description: '',
    id: null,
    milestone_category_id: ['', Validators.required],
    milestone_id: null,
    name: '',
    revised_end_date: null,
    revised_start_date: null,
    track_from_milestone_id: null,
  });
  public ref = inject<
    CustomOverlayRef<
      unknown,
      {
        endDate: null | string;
        forbiddenNames?: string[];
        formValue?: TimelineDialogFormValue;
        milestone_id?: string;
        mode: 'add' | 'edit';
        startDate: null | string;
        timeline_milestone_id?: string;
      }
    >
  >(CustomOverlayRef);
  customErrorMessages = {
    endDate: () => {
      const str = dayjs(this.ref.data?.endDate).format('MM/01/YYYY');

      return `Trial Timeline cannot be shifted before ${str}`;
    },
    startDate: () => {
      const str = dayjs(this.ref.data?.startDate).endOf('month').format('MM/DD/YYYY');

      return `Trial Timeline cannot be shifted after ${str}`;
    },
  };
  endDateValidators: ValidatorFn[] = [
    Validators.required,
    (control: AbstractControl): null | ValidationErrors => {
      const value = control.value;

      if (value === '' || value == null) {
        return null;
      }

      let isInvalid = false;

      if (this.ref.data?.endDate) {
        isInvalid = dayjs(value).startOf('month').isBefore(this.ref.data.endDate);
      }

      return isInvalid ? { endDate: true } : null;
    },
  ];
  forbiddenNames: string[] = [];
  @ViewChild('name') milestoneNameInput!: InputComponent;
  mode: 'add' | 'edit' = 'edit';
  startDateValidators: ValidatorFn[] = [
    Validators.required,
    (control: AbstractControl): null | ValidationErrors => {
      const value = control.value;

      if (value === '' || value == null) {
        return null;
      }

      let isInvalid = false;

      if (this.ref.data?.startDate) {
        isInvalid = dayjs(value).startOf('month').isAfter(this.ref.data.startDate);
      }

      return isInvalid ? { startDate: true } : null;
    },
  ];
  trackFromMilestones$: Observable<MilestoneModel[]> =
    this.addTimelineMilestoneForm.valueChanges.pipe(
      startWith(this.addTimelineMilestoneForm.value as TimelineDialogFormValue),
      map(() => {
        const value: TimelineDialogFormValue = this.addTimelineMilestoneForm.getRawValue();

        if (value.contract_start_date) {
          return this.timelineQuery
            .getValue()
            .items.filter((x) => {
              return (
                new Date(x.contract_end_date) <= new Date(value.contract_start_date) &&
                x.milestone.id !== value.milestone_id
              );
            })
            .map((x) => this.milestoneQuery.getEntity(x.milestone.id)) as MilestoneModel[];
        }

        return [];
      })
    );

  constructor() {
    if (this.ref.data?.mode) {
      this.mode = this.ref.data.mode;
    }

    if (this.ref.data?.formValue) {
      this.addTimelineMilestoneForm.patchValue(this.ref.data?.formValue);
    }

    if (this.ref.data?.forbiddenNames) {
      this.forbiddenNames = this.ref.data.forbiddenNames;
    }
  }

  // Prevents milestones tracking from other milestones that
  // they're already directly or indirectly tracked from
  createDependencyGraph: TimelineDialogDependencyGraphFn = (
    initialMilestoneId: string,
    timelineItems: listTimelineMilestonesQuery[]
  ) => {
    // Store dependent milestone ids
    const dependentIds: string[] = [];

    // Recursive function to find directly and
    // indirectly dependent milestones
    const findDependentIds: TimelineDialogDependencyFn = (milestoneId: string) => {
      const dependentItems = timelineItems.filter(
        (item) => item.track_from_milestone?.id === milestoneId
      );

      if (!dependentItems.length) {
        return;
      }

      dependentItems.forEach((item) => {
        const id = item.milestone.id;

        if (!dependentIds.includes(id)) {
          dependentIds.push(id);
          findDependentIds(id);
        }
      });
    };

    // Find all dependent milestone ids
    findDependentIds(initialMilestoneId);

    // Return dependent milestone ids
    return dependentIds;
  };

  onCancel() {
    this.ref.close();
  }

  // eslint-disable-next-line max-lines-per-function
  async onSubmit() {
    if (this.addTimelineMilestoneForm.valid) {
      const data: TimelineDialogFormValue = this.addTimelineMilestoneForm.value;

      if (data.contract_end_date < data.contract_start_date) {
        this.overlayService.error(MessagesConstants.START_DATE_BEFORE_END);

        return;
      }

      if (this.forbiddenNames.includes(data.name.toString())) {
        this.milestoneNameInput.fc.setErrors({ duplicatedMilestoneNames: true });

        return;
      }

      if (
        this.mode === 'edit' &&
        this.ref.data?.timeline_milestone_id &&
        this.ref.data.milestone_id
      ) {
        const { formValue, milestone_id, timeline_milestone_id } = this.ref.data;
        const updateDependencyInputs: UpdateDependencyInput[] = [];

        if (formValue) {
          const arr = (['revised', 'contract', 'actual'] as const).map((str) => ({
            bool: data[`${str}_end_date`] !== formValue[`${str}_end_date`],
            name: str,
          }));

          let { items } = this.timelineQuery.getValue();

          // Find the milestone that this milestone
          // has selected to track from
          const tracked_milestone = items.find(
            (x) => x.milestone.id === data.track_from_milestone_id
          );

          // Find the milestones (dependent milestones)
          // that are tracking directly or indirectly from this milestone
          const dependencyGraph = this.createDependencyGraph(milestone_id, items);

          // This milestone cannot track from a milestone that
          // it's already being tracked from (dependent milestones)
          if (tracked_milestone && dependencyGraph.includes(tracked_milestone.milestone.id)) {
            const formControls = this.addTimelineMilestoneForm.controls;

            formControls.track_from_milestone_id.setErrors({ milestonesTrackEachOther: true });

            return;
          }

          // Confirm tracked-from milestone dates (dependent milestones)
          if (tracked_milestone && data.contract_start_date < tracked_milestone.contract_end_date) {
            this.overlayService.error('Start Date must be after tracked milestone End Date');

            return;
          }

          // Find the dependent milestones that are
          // tracking (directly only) from this milestone
          items = items.filter((x) => x.track_from_milestone?.id === milestone_id);

          // Update dependent milestones
          if (items.length > 0 && arr.some((x) => x.bool)) {
            for (const { bool, name } of arr) {
              if (bool) {
                const response = await firstValueFrom(
                  this.overlayService.openPopup<
                    {
                      field_name: string;
                      id: string;
                      milestone_id: string;
                      newFormValue: TimelineDialogFormValue;
                      oldFormValue: TimelineDialogFormValue;
                    },
                    UpdateDependencyInput[]
                  >({
                    content: TimelineDependencyComponent,
                    data: {
                      field_name: name,
                      id: timeline_milestone_id,
                      milestone_id,
                      newFormValue: data,
                      oldFormValue: formValue,
                    },
                    settings: {
                      header: 'Apply to Dependent Phases?',
                      primaryButton: {
                        action: (instance: unknown) =>
                          (instance as TimelineDependencyComponent)?.onSave(),
                        label: 'Save Milestone Changes',
                      },
                      secondaryButton: { hidden: true },
                    },
                  }).afterClosed$
                );

                if (response.data) {
                  updateDependencyInputs.push(...response.data);
                } else {
                  return;
                }
              }
            }
          }
        }

        const { success } = this.timelineService.updateTimelineMilestone({
          ...data,
          id: timeline_milestone_id,
          milestone_id,
        });

        if (success) {
          this.ref.close({
            update: {
              ...data,
              id: timeline_milestone_id,
              milestone_id,
            },
            updateDependency: updateDependencyInputs,
          });
        }
      } else {
        const { createdMilestoneId, milestoneId, success } =
          this.timelineService.createTimelineMilestone(data);

        if (success) {
          this.ref.close({ ...data, id: createdMilestoneId, milestone_id: milestoneId });
        }
      }
    }
  }
}
